import React, { useState, useEffect } from 'react';
import { ShareFatIcon } from '@phosphor-icons/react';
import {

    Dialog,
    Box,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Slide,
    CircularProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Button
} from '@mui/material';
import { keyframes } from '@mui/system';
import { Close, Undo, PlayArrow, Star, Add, Check, Replay } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { discoverMovies, getMovieDetails } from '../../services/tmdbFunctionsApi'; // Ensure this path is correct
import { useUserWatchlistData } from '../../contexts/UserWatchlistDataContext';
import { useUserMovieData } from '../../contexts/UserMovieDataContext';
import CustomButton from './CustomButton';
import { DotLottiePlayer } from '@dotlottie/react-player';
import WatchlistMultiSelectDialog from '../dialogs/WatchlistMultiSelectDialog';
import MarkAsWatchedDialog from '../dialogs/MarkAsWatchedDialog';
import { TMDbMovie } from '../../services/tmdbApi';
import { StarBorder } from '@mui/icons-material';
import { font } from '../../utils/fontHelper';

// Animations
const pickWinner = keyframes`
  0%, 20% { transform: scale(1) translateY(0); opacity: 1; border-color: inherit; z-index: 1; }
  30%, 50% { transform: scale(1.05) translateY(-5px); opacity: 1; border-color: inherit; box-shadow: 0 8px 32px rgba(0,0,0,0.2); z-index: 10; }
  60%, 100% { transform: scale(0.95); opacity: 0; }
`;

const pickLoser = keyframes`
  0%, 20% { transform: scale(1); opacity: 1; filter: grayscale(0%); border-color: inherit; }
  30%, 50% { transform: scale(0.95); opacity: 0.4; filter: grayscale(100%); border-color: inherit; }
  60%, 100% { transform: scale(0.95); opacity: 0; }
`;

// Transition for full screen dialog
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface Movie {
    id: number | string;
    title: string;
    poster_path: string | null;
    posterUrl?: string; // Full URL for non-TMDB images
    release_date?: string;
    vote_average?: number;
    original_language?: string;
}

interface ThisOrThatGameDialogProps {
    open: boolean;
    onClose: () => void;
    onWinnerDecided: (winner: { posterPath: string; title: string }) => void;
    userPreferredLanguages?: string[];
}

type GameState = 'setup' | 'loading' | 'playing' | 'winner';

// All available languages for the game
const ALL_AVAILABLE_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French' },
    { code: 'es', label: 'Spanish' },
    { code: 'hi', label: 'Hindi' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'ta', label: 'Tamil' },
    { code: 'te', label: 'Telugu' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ru', label: 'Russian' },
    { code: 'ar', label: 'Arabic' },
    { code: 'tr', label: 'Turkish' },
];

// Helper to get displayed languages based on user preferences
const getDisplayedLanguages = (userPreferredLanguages: string[]): { code: string; label: string }[] => {
    // Get languages that match user preferences
    const preferredLangs = ALL_AVAILABLE_LANGUAGES.filter(lang =>
        userPreferredLanguages.includes(lang.code)
    );

    // If user has less than 3 preferred languages, add random ones to make minimum 5 total
    if (preferredLangs.length < 3) {
        const nonPreferredLangs = ALL_AVAILABLE_LANGUAGES.filter(lang =>
            !userPreferredLanguages.includes(lang.code)
        );
        // Shuffle and take enough to make total 5
        const shuffled = nonPreferredLangs.sort(() => 0.5 - Math.random());
        const needed = 5 - preferredLangs.length;
        const additionalLangs = shuffled.slice(0, needed);
        return [...preferredLangs, ...additionalLangs];
    }

    return preferredLangs;
};

const ThisOrThatGameDialog: React.FC<ThisOrThatGameDialogProps> = ({
    open,
    onClose,
    onWinnerDecided,
    userPreferredLanguages = ['en']
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    const { userWatchlists: contextUserWatchlists } = useUserWatchlistData();

    // Compute displayed languages based on user preferences
    // If user has < 3 preferred languages, add random ones to make minimum 5 total
    const displayedLanguages = React.useMemo(() => {
        return getDisplayedLanguages(userPreferredLanguages);
    }, [userPreferredLanguages]);

    const [gameState, setGameState] = useState<GameState>('setup');

    // Setup State
    const [onlyWatched, setOnlyWatched] = useState(false);
    const [languages, setLanguages] = useState<string[]>(() => {
        // Pre-select user's preferred languages if any are in the displayed set
        const preferred = userPreferredLanguages.filter(code =>
            ALL_AVAILABLE_LANGUAGES.some(l => l.code === code)
        );
        return preferred.length > 0 ? preferred : ['en'];
    });

    // Sync language selection when user preferences load asynchronously
    useEffect(() => {
        if (gameState !== 'setup') return;
        const preferred = userPreferredLanguages.filter(code =>
            ALL_AVAILABLE_LANGUAGES.some(l => l.code === code)
        );
        if (preferred.length > 0) {
            setLanguages(preferred);
        }
    }, [userPreferredLanguages, gameState]);

    // Game Data
    const [movies, setMovies] = useState<Movie[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentWinner, setCurrentWinner] = useState<Movie | null>(null);
    const [challenger, setChallenger] = useState<Movie | null>(null);
    const [history, setHistory] = useState<{
        winner: Movie;
        loser: Movie;
        roundIndex: number
    }[]>([]);
    const [round, setRound] = useState(1);

    const [selectionState, setSelectionState] = useState<{ selectedId: string | number, animating: boolean } | null>(null);
    const MAX_ROUNDS = 7;
    const MIN_WATCHED_MOVIES = 15;

    // Action sheets state
    const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
    const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
    const [selectedMovieForAction, setSelectedMovieForAction] = useState<Movie | null>(null);

    const { movieDataCache, getUserMovieData, markAsWatched } = useUserMovieData();

    // Calculate watched movies count
    const watchedMovies = React.useMemo(() => {
        return Array.from(movieDataCache.values())
            .filter((m: any) => m.isWatched)
            .map((m: any) => ({
                id: m.movieId,
                title: m.title || m.Title || 'Unknown',
                poster_path: m.posterPath || m.poster_path || null,
                posterUrl: m.posterUrl || (m.posterPath ? `https://image.tmdb.org/t/p/w500${m.posterPath}` : null) || (m.poster_path ? `https://image.tmdb.org/t/p/w500${m.poster_path}` : null),
                original_language: m.original_language || 'en'
            }));
    }, [movieDataCache]);

    const watchedMoviesCount = watchedMovies.length;
    const canUseWatched = watchedMoviesCount >= MIN_WATCHED_MOVIES;

    // Initialize game
    const handleStartGame = async () => {
        setGameState('loading');

        try {
            let pool: Movie[] = [];

            if (onlyWatched && canUseWatched) {
                // Collect movies from "watched" list
                // Shuffle first to get random selection
                let candidates = [...watchedMovies].sort(() => 0.5 - Math.random());

                // Limit to 20 candidates to avoid excessive fetching
                candidates = candidates.slice(0, 20);

                // Hydrate movie details if missing
                const hydratedCandidates = await Promise.all(candidates.map(async (m) => {
                    // If we already have valid data, use it
                    if (m.title !== 'Unknown' && m.posterUrl) {
                        return m;
                    }

                    // Otherwise fetch details
                    try {
                        const details = await getMovieDetails(m.id);
                        return {
                            ...m,
                            title: details.title,
                            poster_path: details.poster_path, // Type casting/renaming might be needed depending on Movie interface
                            posterUrl: details.poster_path ? `https://image.tmdb.org/t/p/w500${details.poster_path}` : null,
                            original_language: (details as any).original_language || 'en'
                        };
                    } catch (e) {
                        console.warn(`Failed to hydrate movie ${m.id}`, e);
                        return null;
                    }
                }));

                pool = hydratedCandidates.filter((m) => !!m && !!m.posterUrl) as Movie[];

                // Filter by selected languages if not empty
                if (languages.length > 0) {
                    pool = pool.filter(m => !m.original_language || languages.includes(m.original_language));
                }
            } else {
                // Fetch from API - Popular/Good movies
                // Match the successful Quick Takes approach with relaxed thresholds
                const hasEnglish = languages.includes('en');
                const hasOnlyEnglish = languages.length === 1 && languages[0] === 'en';

                // Use the same thresholds as Quick Takes for consistency
                const voteCountThreshold = hasOnlyEnglish ? 100 : 30;

                // Try multiple pages to get more movies
                const pagesToFetch = 3;
                const allMovies: Movie[] = [];

                for (let i = 0; i < pagesToFetch; i++) {
                    const randomPage = Math.floor(Math.random() * 10) + 1;
                    const langParam = languages.length > 0 ? languages.join('|') : 'en';

                    try {
                        const data = await discoverMovies({
                            page: randomPage,
                            with_original_language: langParam, // Support multi-lang via pipe
                            sort_by: 'popularity.desc', // Use popularity for better results
                            include_adult: false,
                            'vote_count.gte': voteCountThreshold // Match Quick Takes thresholds
                        });

                        if (data && data.results) {
                            const newMovies = data.results.map((m: any) => ({
                                id: m.id,
                                title: m.title,
                                poster_path: m.poster_path,
                                posterUrl: m.poster_path ? `https://image.tmdb.org/t/p/w500${m.poster_path}` : null,
                                vote_average: m.vote_average,
                                release_date: m.release_date,
                                original_language: m.original_language
                            })).filter((m: any) => m.posterUrl);

                            allMovies.push(...newMovies);
                        }
                    } catch (error) {
                        console.warn(`Failed to fetch page ${randomPage}:`, error);
                    }
                }

                // Remove duplicates based on movie ID
                const uniqueMovies = Array.from(
                    new Map(allMovies.map(m => [m.id, m])).values()
                );

                pool = uniqueMovies;
            }

            if (pool.length < 2) {
                alert("Not enough movies found for these criteria! Please try selecting different languages or toggling the 'Only movies I've watched' option.");
                setGameState('setup');
                return;
            }

            // Shuffle pool if from API (though random page helps)
            if (!onlyWatched) {
                pool = pool.sort(() => 0.5 - Math.random());
            }

            // Initialize Round 1
            setMovies(pool);
            setCurrentWinner(pool[0]);
            setChallenger(pool[1]);
            setCurrentIndex(1);
            setRound(1);
            setHistory([]);
            setGameState('playing');

        } catch (e) {
            console.error("Failed to start game", e);
            setGameState('setup');
        }
    };

    const handleLanguageToggle = (code: string) => {
        setLanguages(prev => {
            if (prev.includes(code)) {
                // Don't allow empty selection, default to en if removing last
                if (prev.length === 1) return prev;
                return prev.filter(l => l !== code);
            } else {
                return [...prev, code];
            }
        });
    };

    const handleChoice = (selected: Movie) => {
        if (!currentWinner || !challenger || selectionState?.animating) return;

        // Trigger animation
        setSelectionState({ selectedId: selected.id, animating: true });

        // Wait for animation
        setTimeout(() => {
            const loser = selected.id === currentWinner.id ? challenger : currentWinner;

            setHistory(prev => [...prev, {
                winner: selected,
                loser: loser,
                roundIndex: currentIndex
            }]);

            setCurrentWinner(selected);

            const nextIndex = currentIndex + 1;

            if (round >= MAX_ROUNDS) {
                // Max rounds reached - game ends
                onWinnerDecided({
                    posterPath: selected.poster_path || '',
                    title: selected.title
                });
                setGameState('winner');
            } else if (nextIndex >= movies.length) {
                // Ran out of new movies - recycle previous losers from history
                const losers = history.map(h => h.loser);

                if (losers.length > 0) {
                    // Shuffle losers and add them back to the pool
                    const shuffledLosers = [...losers].sort(() => 0.5 - Math.random());
                    const expandedPool = [...movies, ...shuffledLosers];

                    setMovies(expandedPool);
                    setChallenger(expandedPool[nextIndex]);
                    setCurrentIndex(nextIndex);
                    setRound(r => r + 1);
                } else {
                    // No losers to recycle, end game
                    onWinnerDecided({
                        posterPath: selected.poster_path || '',
                        title: selected.title
                    });
                    setGameState('winner');
                }
            } else {
                setChallenger(movies[nextIndex]);
                setCurrentIndex(nextIndex);
                setRound(r => r + 1);
            }
            setSelectionState(null);
        }, 1500);
    };



    const handleUndo = () => {
        if (history.length === 0) return;

        const lastMove = history[history.length - 1];
        const newHistory = history.slice(0, -1);
        const prevChallengerIndex = currentIndex - 1;
        const prevChallenger = movies[prevChallengerIndex];
        const other = lastMove.winner.id === prevChallenger.id ? lastMove.loser : lastMove.winner;

        setHistory(newHistory);
        setChallenger(prevChallenger);
        setCurrentWinner(other);
        setCurrentIndex(prevChallengerIndex);
        setRound(r => r - 1);
    };

    const handleOpenWatchlist = (e: React.MouseEvent, movie: Movie) => {
        e.stopPropagation();
        setSelectedMovieForAction(movie);
        setWatchlistDialogOpen(true);
    };

    const handleOpenRating = (e: React.MouseEvent, movie: Movie) => {
        e.stopPropagation();
        setSelectedMovieForAction(movie);
        setRatingDialogOpen(true);
    };

    const renderMovieActions = (movie: Movie) => {
        const userMovieData = getUserMovieData(String(movie.id));
        const inWatchlist = userMovieData?.isInWatchlist;
        const isRated = userMovieData?.isWatched || (userMovieData?.rating !== undefined && userMovieData.rating > 0);

        if (isRated) return null;

        return (
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 1.5,
                    justifyContent: 'center',
                    opacity: 0.9,
                    transition: 'opacity 0.2s',
                    '&:hover': { opacity: 1 }
                }}
            >
                {!inWatchlist && (
                    <Button
                        size="small"
                        startIcon={<Add sx={{ fontSize: 16 }} />}
                        onClick={(e) => handleOpenWatchlist(e, movie)}
                        sx={{
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                            fontSize: 12,
                            py: 0.5,
                            px: 1.5,
                            minWidth: 0,
                            borderRadius: 20,
                            textTransform: 'none',
                            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            '&:hover': {
                                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                            }
                        }}
                    >
                        Add
                    </Button>
                )}
                <Button
                    size="small"
                    startIcon={<StarBorder sx={{ fontSize: 16 }} />}
                    onClick={(e) => handleOpenRating(e, movie)}
                    sx={{
                        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        fontSize: 12,
                        py: 0.5,
                        px: 1.5,
                        minWidth: 0,
                        borderRadius: 20,
                        textTransform: 'none',
                        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        '&:hover': {
                            bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                            borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                        }
                    }}
                >
                    Rate
                </Button>
            </Box>
        );
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setGameState('setup');
            setRound(1);
            setHistory([]);
        }, 300);
    };

    return (
        <Dialog
            fullScreen={isMobile}
            open={open}
            onClose={handleClose}
            TransitionComponent={isMobile ? Transition : undefined}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 4,
                    maxWidth: isMobile ? '100%' : '800px',
                    width: isMobile ? '100%' : '800px',
                    height: isMobile ? '100%' : 'auto',
                    minHeight: isMobile ? '100%' : '600px',
                    maxHeight: isMobile ? '100%' : '90vh',
                    backgroundImage: gameState === 'setup'
                        ? (isDark
                            ? 'linear-gradient(135deg, #5c206aff 0%, #330e59ff 100%)'
                            : 'linear-gradient(135deg, #e8eaf6 0%, #FFF5CC 100%)')
                        : (isDark
                            ? 'linear-gradient(180deg, #1E1E1E 0%, #121212 100%)'
                            : 'linear-gradient(180deg, #FFFFFF 0%, #F5F5F7 100%)'),
                }
            }}
            BackdropProps={{
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
        >
            {/* Header - with safe area support for fullScreen mode */}
            <Box sx={{
                px: 3,
                // Include safe-area-inset-top for iOS PWA status bar in fullScreen mode
                pt: isMobile ? 'calc(var(--safe-area-inset-top) + 16px)' : 2,
                pb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                position: 'relative'
            }}>
                <Box sx={{ width: 40 }}>
                    {gameState === 'playing' && history.length > 0 && (
                        <IconButton onClick={handleUndo} size="small">
                            <Undo />
                        </IconButton>
                    )}
                </Box>

                <Typography sx={{
                    fontFamily: font(),
                    fontWeight: 700,
                    fontSize: 18,
                    textAlign: 'center',
                    opacity: gameState === 'setup' ? 0 : 1, // Hide title on start screen
                    transition: 'opacity 0.3s'
                }}>
                    This or That
                </Typography>

                <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton onClick={handleClose} size="small">
                        <Close />
                    </IconButton>
                </Box>

                {gameState === 'playing' && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: 2,
                        bgcolor: 'primary.main',
                        width: `${(round / MAX_ROUNDS) * 100}%`,
                        transition: 'width 0.3s ease'
                    }} />
                )}
            </Box>

            {/* Content Area */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                position: 'relative'
            }}>

                {/* VIEW: SETUP */}
                {gameState === 'setup' && (
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        gap: 4
                    }}>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Typography variant="h4" sx={{
                                fontFamily: font(),
                                fontWeight: 800,
                                mb: 2,
                                background: isDark
                                    ? 'linear-gradient(45deg, #bbb, #fff)'
                                    : 'linear-gradient(45deg, #333, #000)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent'
                            }}>
                                This or That
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', maxWidth: 400 }}>
                                Select your preferences below. The winner stays until the end.
                            </Typography>
                        </Box>

                        <Box sx={{
                            width: '100%',
                            maxWidth: 400,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            p: 3,
                            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            borderRadius: 4,
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
                        }}>
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={onlyWatched}
                                            disabled={!canUseWatched}
                                            onChange={(e) => setOnlyWatched(e.target.checked)}
                                        />
                                    }
                                    label={<Typography sx={{ fontWeight: 500 }}>Only movies I've watched</Typography>}
                                    sx={{ justifyContent: 'space-between', ml: 0, width: '100%' }}
                                    labelPlacement="start"
                                />
                                {!canUseWatched && (
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
                                        Mark at least {MIN_WATCHED_MOVIES} movies as watched to enable this option.
                                    </Typography>
                                )}
                            </Box>

                            <Box>

                                <Box sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    justifyContent: 'center'
                                }}>
                                    {displayedLanguages.map((lang) => {
                                        const isSelected = languages.includes(lang.code);
                                        return (
                                            <Box
                                                key={lang.code}
                                                onClick={() => handleLanguageToggle(lang.code)}
                                                sx={{
                                                    px: 2,
                                                    py: 0.75,
                                                    borderRadius: 20,
                                                    cursor: 'pointer',
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    border: `1px solid ${isSelected
                                                        ? (isDark ? '#fff' : '#000')
                                                        : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)')}`,
                                                    bgcolor: isSelected
                                                        ? (isDark ? '#fff' : '#000')
                                                        : 'transparent',
                                                    color: isSelected
                                                        ? (isDark ? '#000' : '#fff')
                                                        : 'text.primary',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    '&:hover': {
                                                        bgcolor: isSelected
                                                            ? (isDark ? '#fff' : '#000')
                                                            : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)')
                                                    }
                                                }}
                                            >
                                                {isSelected && <Check sx={{ fontSize: 16 }} />}
                                                {lang.label}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>

                        <CustomButton
                            semantic="brand"
                            size="medium"
                            onClick={handleStartGame}
                            label="Start Game"
                            icon={<PlayArrow />}
                            sx={{ mt: 2, minWidth: 200, py: 1.5 }}
                        />
                    </Box>
                )}

                {/* VIEW: LOADING */}
                {gameState === 'loading' && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <Box sx={{ display: 'flex', gap: 3, perspective: 1000 }}>
                            {/* Card 1 (Winner) */}
                            <Box sx={{
                                width: 90, height: 135,
                                background: isDark ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 100%)' : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.01) 100%)',
                                borderRadius: 3,
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                animation: `${pickWinner} 4s infinite ease-in-out`,
                            }}>
                                <PlayArrow sx={{ opacity: 0.3, fontSize: 32, color: isDark ? 'white' : 'black' }} />
                            </Box>

                            {/* Card 2 (Loser) */}
                            <Box sx={{
                                width: 90, height: 135,
                                background: isDark ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 100%)' : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.01) 100%)',
                                borderRadius: 3,
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                animation: `${pickLoser} 4s infinite ease-in-out`,
                            }}>
                                <PlayArrow sx={{ opacity: 0.3, fontSize: 32, color: isDark ? 'white' : 'black' }} />
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'center', px: 4 }}>
                            <Typography sx={{ color: isDark ? 'white' : 'black', fontFamily: font(), fontWeight: 700, fontSize: 20, mb: 1 }}>
                                This or That
                            </Typography>
                            <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontFamily: font(), fontSize: 15, maxWidth: 240, mx: 'auto', lineHeight: 1.4, mb: 2 }}>
                                Choose your favorite film from head-to-head matchups
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <CircularProgress size={16} sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)' }} />
                                <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)', fontFamily: font(), fontSize: 13 }}>
                                    Loading...
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* VIEW: PLAYING */}
                {gameState === 'playing' && currentWinner && challenger && (
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: { xs: 1, sm: 2, md: 3 }, // Added padding
                        overflowY: 'auto'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start', // Align to top to keep posters aligned
                            justifyContent: 'center',
                            gap: { xs: 2, sm: 4, md: 8 }, // Significantly increased gap
                            height: 'auto',
                            width: '100%',
                            position: 'relative',
                            mt: 2
                        }}>
                            {/* Winner (Left) */}
                            <Box
                                onClick={() => handleChoice(currentWinner)}
                                sx={{
                                    flex: 1,
                                    maxWidth: { xs: '50vw', sm: '240px', md: '300px' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transform: selectionState?.selectedId === currentWinner.id ? 'scale(1.05)' : 'scale(1)',
                                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    '&:hover': {
                                        transform: 'scale(1.03)'
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98)'
                                    }
                                }}
                            >
                                <Box sx={{
                                    width: '100%',
                                    aspectRatio: '2/3',
                                    borderRadius: 4, // Added border radius
                                    overflow: 'hidden',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)', // Improved shadow
                                    mb: 2, // Space for title
                                    bgcolor: '#000',
                                    position: 'relative'
                                }}>
                                    <Box component="img"
                                        src={currentWinner.posterUrl}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }} // Changed to cover for full bleed
                                    />
                                    {selectionState?.selectedId === currentWinner.id && (
                                        <Box sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 20,
                                            pointerEvents: 'none'
                                        }}>
                                            <Box sx={{ width: '100%', height: '100%' }}>
                                                <DotLottiePlayer
                                                    src="/animations/love_particle.lottie"
                                                    autoplay
                                                    loop={false}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                <Typography sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 14, sm: 16, md: 18 },
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    color: 'text.primary',
                                    px: 1
                                }}>
                                    {currentWinner.title}
                                </Typography>
                                {renderMovieActions(currentWinner)}
                            </Box>

                            {/* OR Badge */}
                            <Box sx={{
                                position: 'absolute',
                                top: { xs: '35%', sm: '40%' }, // Position relative to poster
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: { xs: 40, md: 56 },
                                height: { xs: 40, md: 56 },
                                borderRadius: '50%',
                                bgcolor: isDark ? '#ffffffff' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                zIndex: 10,
                                border: `4px solid ${isDark ? '#121212' : '#F5F5F7'}` // Match background
                            }}>
                                <Typography sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: 12, md: 14 },
                                    color: isDark ? '#2a2a2aff' : '#666'
                                }}>
                                    OR
                                </Typography>
                            </Box>

                            {/* Challenger (Right) */}
                            <Box
                                onClick={() => handleChoice(challenger)}
                                sx={{
                                    flex: 1,
                                    maxWidth: { xs: '50vw', sm: '240px', md: '300px' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transform: selectionState?.selectedId === challenger.id ? 'scale(1.05)' : 'scale(1)',
                                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    '&:hover': {
                                        transform: 'scale(1.03)'
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98)'
                                    }
                                }}
                            >
                                <Box sx={{
                                    width: '100%',
                                    aspectRatio: '2/3',
                                    borderRadius: 4, // Added border radius
                                    overflow: 'hidden',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)', // Improved shadow
                                    mb: 2,
                                    bgcolor: '#000',
                                    position: 'relative'
                                }}>
                                    <Box component="img"
                                        src={challenger.posterUrl}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }} // Changed to cover
                                    />
                                    {selectionState?.selectedId === challenger.id && (
                                        <Box sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            zIndex: 20,
                                            pointerEvents: 'none'
                                        }}>
                                            <Box sx={{ width: '100%', height: '100%' }}>
                                                <DotLottiePlayer
                                                    src="/animations/love_particle.lottie"
                                                    autoplay
                                                    loop={false}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                <Typography sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 14, sm: 16, md: 18 },
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    color: 'text.primary',
                                    px: 1
                                }}>
                                    {challenger.title}
                                </Typography>
                                {renderMovieActions(challenger)}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* VIEW: WINNER */}
                {gameState === 'winner' && currentWinner && (
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto'
                    }}>
                        {/* Winner Header */}
                        <Box sx={{
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            background: 'radial-gradient(circle at center, rgba(255,215,0,0.1) 0%, rgba(0,0,0,0) 70%)',
                            flexShrink: 0
                        }}>
                            <Typography sx={{
                                fontWeight: 900,
                                letterSpacing: 2,
                                color: '#FFD700', // Gold
                                mb: 2,
                                textTransform: 'uppercase'
                            }}>
                                Your Winner
                            </Typography>

                            <Box sx={{
                                width: 160,
                                height: 240,
                                borderRadius: 4,
                                overflow: 'hidden',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                                mb: 2,
                                position: 'relative',
                                opacity: 0,
                                transform: 'scale(0.8)',
                                animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s forwards',
                                '@keyframes scaleIn': {
                                    '0%': { opacity: 0, transform: 'scale(0.8)' },
                                    '100%': { opacity: 1, transform: 'scale(1)' }
                                }
                            }}>
                                <Box component="img"
                                    src={currentWinner.posterUrl}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                {/* Winner Badge - Animated Star */}
                                <Box sx={{
                                    position: 'absolute', top: -10, right: -10,
                                    bgcolor: 'background.paper',
                                    borderRadius: '50%',
                                    width: 48, height: 48,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                    animation: 'popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
                                    '@keyframes popIn': {
                                        '0%': { transform: 'scale(0)' },
                                        '100%': { transform: 'scale(1)' }
                                    }
                                }}>
                                    <Star sx={{ fontSize: 28, color: '#FFD700' }} />
                                </Box>
                            </Box>

                            <Typography variant="h5" sx={{
                                fontWeight: 800,
                                textAlign: 'center',
                                mb: 1, // Reduced margin
                                fontSize: 22,
                                maxWidth: '80%',
                                opacity: 0,
                                animation: 'slideUp 0.5s ease 0.3s forwards',
                                '@keyframes slideUp': {
                                    '0%': { opacity: 0, transform: 'translateY(20px)' },
                                    '100%': { opacity: 1, transform: 'translateY(0)' }
                                }
                            }}>
                                {currentWinner.title}
                            </Typography>

                            <Box sx={{
                                mb: 3,
                                opacity: 0,
                                animation: 'slideUp 0.5s ease 0.35s forwards'
                            }}>
                                {renderMovieActions(currentWinner)}
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                mb: 3,
                                opacity: 0,
                                animation: 'slideUp 0.5s ease 0.4s forwards'
                            }}>
                                <CustomButton
                                    semantic="brand"
                                    icon={<ShareFatIcon />}
                                    label="Share Game"
                                    onClick={() => {
                                        if (navigator.share) {
                                            navigator.share({
                                                title: 'This or That Game',
                                                text: `Play This or That on Watchlists! My winner was ${currentWinner.title}.`,
                                                url: window.location.href
                                            });
                                        } else {
                                            alert("Screenshot to share!");
                                        }
                                    }}
                                />
                                <CustomButton
                                    semantic="tertiary"
                                    icon={<Replay />}
                                    label="Play Again"
                                    onClick={() => {
                                        setGameState('setup');
                                        setRound(1);
                                        setHistory([]);
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Round History List */}
                        <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                            <Typography sx={{ px: 2, pb: 1, fontWeight: 700, opacity: 0.7, fontSize: 14 }}>
                                ROUNDS PLAYED ({history.length})
                            </Typography>
                            <List disablePadding>
                                {history.map((record, idx) => {
                                    const movieId = String(record.loser.id);
                                    const userMovieData = getUserMovieData(movieId);
                                    const isWatched = userMovieData?.isWatched;
                                    const isInWatchlist = userMovieData?.isInWatchlist;

                                    return (
                                        <ListItem key={idx} sx={{
                                            bgcolor: 'background.paper',
                                            mb: 1,
                                            borderRadius: 2,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                        }}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    src={record.loser.posterUrl}
                                                    variant="rounded"
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={record.loser.title}
                                                secondary={`Eliminated in Round ${idx + 1}`}
                                                primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {!isWatched && (
                                                    <>
                                                        {!isInWatchlist && (
                                                            <IconButton
                                                                onClick={(e) => handleOpenWatchlist(e, record.loser)}
                                                                size="small"
                                                            >
                                                                <Add fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                        <IconButton
                                                            onClick={(e) => handleOpenRating(e, record.loser)}
                                                            size="small"
                                                        >
                                                            <StarBorder fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    </Box>
                )
                }
            </Box>


            {/* Action Dialogs */}
            {
                selectedMovieForAction && (
                    <>
                        <WatchlistMultiSelectDialog
                            open={watchlistDialogOpen}
                            onClose={() => setWatchlistDialogOpen(false)}
                            movieId={String(selectedMovieForAction.id)}
                            movieTitle={selectedMovieForAction.title}
                            moviePoster={selectedMovieForAction.poster_path || undefined}
                            movieYear={selectedMovieForAction.release_date?.substring(0, 4)}
                            zIndex={1400}
                            onSave={() => {
                                // Optionally refresh state if needed
                            }}
                        />
                        <MarkAsWatchedDialog
                            open={ratingDialogOpen}
                            onClose={() => setRatingDialogOpen(false)}
                            zIndex={1400}
                            movie={{
                                id: Number(selectedMovieForAction.id),
                                title: selectedMovieForAction.title,
                                Title: selectedMovieForAction.title,
                                poster_path: selectedMovieForAction.poster_path,
                                Poster: selectedMovieForAction.posterUrl,
                                release_date: selectedMovieForAction.release_date || '',
                                Year: selectedMovieForAction.release_date?.substring(0, 4) || 'Unknown',
                                // Basic shims for required props
                                original_title: selectedMovieForAction.title,
                                overview: '',
                                genre_ids: [],
                                adult: false,
                                video: false,
                                original_language: selectedMovieForAction.original_language || 'en',
                                popularity: 0,
                                vote_average: selectedMovieForAction.vote_average || 0,
                                vote_count: 0
                            } as unknown as TMDbMovie}
                            onMarkAsWatched={async (rating, review, isPrivate, visibility, date) => {
                                // The dialog calls onClose but we need to handle the actual data saving if not handled inside
                                // Wait, MarkAsWatchedDialog takes onMarkAsWatched which usually calls context
                                // Let's assume we need to call the context method here
                                // Actually based on typical usage in this app, the dialog usually handles it or calls a prop function which calls context.
                                // Checking MarkAsWatchedDialog usage in other files...
                                // In MarkAsWatchedDialog.tsx, onMarkAsWatched is a prop.
                                // We need access to 'markAsWatched' from UserMovieDataContext
                                await markAsWatched(
                                    String(selectedMovieForAction.id),
                                    selectedMovieForAction.title,
                                    rating,
                                    review,
                                    isPrivate,
                                    selectedMovieForAction.poster_path ? `https://image.tmdb.org/t/p/w500${selectedMovieForAction.poster_path}` : undefined,
                                    selectedMovieForAction.release_date?.substring(0, 4),
                                    visibility,
                                    date
                                );
                            }}
                        />
                    </>
                )
            }
        </Dialog >
    );
};

export default ThisOrThatGameDialog;
